/** A box in the scroll container's content coordinates, in px. */
export type ContentBox = { left: number; top: number; width: number; height: number };

export type ScrollView = { scrollLeft: number; scrollTop: number; width: number; height: number };

/** How long a smooth scroll takes to land, so the pulse starts after it. */
export const SCROLL_SETTLE_MS = 380;

export const prefersReducedMotion = () =>
	typeof window.matchMedia === "function" &&
	window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const TOP_CLEARANCE = 40;
const BOTTOM_CLEARANCE = 24;
const MIN_TOP_OFFSET = 64;
const SIDE_CLEARANCE = 24;

const fitsHorizontally = (box: ContentBox, view: ScrollView) =>
	box.left >= view.scrollLeft && box.left + box.width <= view.scrollLeft + view.width;

/**
 * Where to scroll so the box sits in the top third. Horizontally it moves only when the box is
 * cut off at a side.
 */
export const topThirdTarget = (box: ContentBox, view: ScrollView) => ({
	top: Math.max(0, box.top - Math.max(MIN_TOP_OFFSET, (view.height - box.height) / 3)),
	left: fitsHorizontally(box, view)
		? view.scrollLeft
		: Math.max(0, box.left - Math.max(SIDE_CLEARANCE, (view.width - box.width) / 2)),
});

/**
 * Where to scroll so the box shows, or nothing when it already does. A box counts as visible
 * with 40 px clear at the top and 24 px at the bottom; otherwise it lands in the top third.
 */
export const autoScrollTarget = (
	box: ContentBox,
	view: ScrollView,
): { left: number; top: number } | undefined => {
	const fitsVertically =
		box.top >= view.scrollTop + TOP_CLEARANCE &&
		box.top + box.height <= view.scrollTop + view.height - BOTTOM_CLEARANCE;
	if (fitsVertically && fitsHorizontally(box, view)) return undefined;
	const target = topThirdTarget(box, view);
	return fitsVertically ? { ...target, top: view.scrollTop } : target;
};
